const pending = new Map<string, Promise<void>>();

/**
 * Loads an external script into the document exactly once.
 *
 * This is the one dependency the build cannot bundle. The Probability form is a
 * hosted web component: its field list, validation rules and markup come from
 * the program config on their server, so it has to be fetched at runtime.
 *
 * Two guards matter:
 *
 * - The same `<script>` tag may already be in the document — Probability hand the
 *   snippet out directly, so it can arrive from the markup as well as from here. A
 *   second tag would re-run a 3 MB bundle and re-enter `customElements.define()`,
 *   which throws. An existing tag therefore wins.
 * - Two instances of our element on one page must share one load.
 *
 * Rejects only for a tag we injected ourselves and watched fail. A tag that was
 * already on the page resolves immediately: its outcome is not ours to report,
 * and callers should verify readiness with `customElements.whenDefined` rather
 * than treat this promise as proof the component exists.
 */
export function loadScript(src: string): Promise<void> {
  if (typeof document === "undefined") return Promise.resolve();

  const cached = pending.get(src);
  if (cached) return cached;

  // Compare against the resolved .src so a relative tag in the markup still
  // matches the absolute URL we ask for.
  const existing = [...document.querySelectorAll("script")].some((tag) => tag.src === src);

  const promise = existing
    ? Promise.resolve()
    : new Promise<void>((resolve, reject) => {
        const script = document.createElement("script");
        script.src = src;
        script.async = true;
        script.addEventListener("load", () => resolve(), { once: true });
        script.addEventListener("error", () => reject(new Error(`Failed to load ${src}`)), {
          once: true,
        });
        document.head.appendChild(script);
      });

  pending.set(src, promise);
  return promise;
}
